import React, { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

export default function StepInfo({ initial, onChange, onNext }) {
  const { t } = useTranslation("publish");

  const [form, setForm] = useState({
    purpose: "buy",
    type: "house",
    title: "",
    description: "",
    price: "",
    rooms: "",
    bedrooms: "",
    bathrooms: "",
    livingArea: "",
    yearBuilt: "",
    ...(initial || {}),
  });

  useEffect(() => {
    if (initial) {
      setForm((prev) => ({
        ...prev,
        ...initial,
      }));
    }
  }, [initial]);

  const required = [
    "purpose",
    "type",
    "title",
    "description",
    "price",
    "rooms",
    "bedrooms",
    "bathrooms",
    "livingArea",
    "yearBuilt",
  ];

  const isFilled = (key) => String(form[key] ?? "").trim() !== "";

  const filledCount = useMemo(() => {
    return required.filter((k) => isFilled(k)).length;
  }, [form]);

  const canNext = useMemo(() => {
    return required.every((k) => isFilled(k));
  }, [form]);

  const updateField = (name, value) => {
    const next = {
      ...form,
      [name]: value,
    };

    setForm(next);
    onChange?.(next);
  };

  const bind = (name) => ({
    name,
    value: form[name] ?? "",
    onChange: (e) => updateField(name, e.target.value),
  });

  const handleNext = () => {
    const payload = {
      ...form,
      price: Number(form.price),
      rooms: Number(form.rooms),
      bedrooms: Number(form.bedrooms),
      bathrooms: Number(form.bathrooms),
      livingArea: Number(form.livingArea),
      yearBuilt: Number(form.yearBuilt),
    };

    onChange?.(payload);
    onNext?.();
  };

  const inputBase =
    "w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-400 dark:focus:border-blue-400 dark:focus:ring-blue-900/30";

  const labelBase =
    "mb-1.5 block text-sm font-semibold text-gray-700 dark:text-gray-200";

  const sectionCard =
    "rounded-3xl border border-gray-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950";

  return (
    <div className="space-y-6">
      <div className={sectionCard}>
        <div className="mb-5">
          <div className="inline-flex rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
            {t("stepInfo.stepLabel", { defaultValue: "Schritt 1" })}
          </div>

          <h2 className="mt-3 text-2xl font-bold text-gray-900 dark:text-white">
            {t("stepInfo.titleMain", {
              defaultValue: "Basisinformationen zur Immobilie",
            })}
          </h2>

          <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-slate-300">
            {t("stepInfo.descriptionMain", {
              defaultValue:
                "Erfassen Sie hier die wichtigsten Eckdaten Ihrer Immobilie. Diese Angaben bilden die Grundlage für das spätere Exposé.",
            })}
          </p>
        </div>

        <div className="mb-6">
          <div className="h-2 rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-2 rounded-full bg-blue-600 transition-all"
              style={{ width: `${(filledCount / required.length) * 100}%` }}
            />
          </div>

          <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
            {filledCount}/{required.length}{" "}
            {t("stepInfo.progressFilled", { defaultValue: "ausgefüllt" })}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <label className={labelBase}>
              {t("stepInfo.purpose", { defaultValue: "Zweck" })}
            </label>
            <select {...bind("purpose")} className={inputBase}>
              <option value="buy">
                {t("stepInfo.buy", { defaultValue: "Kaufen" })}
              </option>
              <option value="rent">
                {t("stepInfo.rent", { defaultValue: "Mieten" })}
              </option>
            </select>
          </div>

          <div>
            <label className={labelBase}>
              {t("stepInfo.type", { defaultValue: "Typ" })}
            </label>
            <select {...bind("type")} className={inputBase}>
              <option value="house">
                {t("stepInfo.house", { defaultValue: "Haus" })}
              </option>
              <option value="apartment">
                {t("stepInfo.apartment", { defaultValue: "Wohnung" })}
              </option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className={labelBase}>
              {t("stepInfo.title", { defaultValue: "Titel" })}
            </label>
            <input
              type="text"
              {...bind("title")}
              className={inputBase}
              placeholder={t("stepInfo.titlePlaceholder", {
                defaultValue: "Zum Beispiel: Moderne 4-Zimmer-Wohnung mit Balkon",
              })}
            />
          </div>

          <div className="md:col-span-2">
            <label className={labelBase}>
              {t("stepInfo.description", { defaultValue: "Beschreibung" })}
            </label>
            <textarea
              rows={6}
              {...bind("description")}
              className={`${inputBase} min-h-[140px] resize-y`}
              placeholder={t("stepInfo.descriptionPlaceholder", {
                defaultValue:
                  "Beschreiben Sie die Immobilie möglichst ansprechend und präzise.",
              })}
            />
          </div>

          <div>
            <label className={labelBase}>
              {t("stepInfo.price", { defaultValue: "Preis (€)" })}
            </label>
            <input
              type="text"
              inputMode="numeric"
              {...bind("price")}
              className={inputBase}
              placeholder={t("stepInfo.pricePlaceholder", {
                defaultValue: "Zum Beispiel: 450000",
              })}
            />
          </div>

          <div>
            <label className={labelBase}>
              {t("stepInfo.livingArea", {
                defaultValue: "Wohnfläche (m²)",
              })}
            </label>
            <input
              type="text"
              inputMode="numeric"
              {...bind("livingArea")}
              className={inputBase}
              placeholder={t("stepInfo.livingAreaPlaceholder", {
                defaultValue: "Zum Beispiel: 125",
              })}
            />
          </div>

          <div>
            <label className={labelBase}>
              {t("stepInfo.rooms", { defaultValue: "Zimmer" })}
            </label>
            <input
              type="text"
              inputMode="numeric"
              {...bind("rooms")}
              className={inputBase}
              placeholder={t("stepInfo.roomsPlaceholder", {
                defaultValue: "Zum Beispiel: 4",
              })}
            />
          </div>

          <div>
            <label className={labelBase}>
              {t("stepInfo.bedrooms", { defaultValue: "Schlafzimmer" })}
            </label>
            <input
              type="text"
              inputMode="numeric"
              {...bind("bedrooms")}
              className={inputBase}
              placeholder={t("stepInfo.bedroomsPlaceholder", {
                defaultValue: "Zum Beispiel: 3",
              })}
            />
          </div>

          <div>
            <label className={labelBase}>
              {t("stepInfo.bathrooms", { defaultValue: "Badezimmer" })}
            </label>
            <input
              type="text"
              inputMode="numeric"
              {...bind("bathrooms")}
              className={inputBase}
              placeholder={t("stepInfo.bathroomsPlaceholder", {
                defaultValue: "Zum Beispiel: 2",
              })}
            />
          </div>

          <div>
            <label className={labelBase}>
              {t("stepInfo.yearBuilt", { defaultValue: "Baujahr" })}
            </label>
            <input
              type="text"
              inputMode="numeric"
              {...bind("yearBuilt")}
              className={inputBase}
              placeholder={t("stepInfo.yearBuiltPlaceholder", {
                defaultValue: "Zum Beispiel: 2018",
              })}
            />
          </div>
        </div>

        <div className="mt-6 rounded-2xl border border-blue-100 bg-blue-50/70 px-4 py-3 text-sm text-blue-900 dark:border-blue-900/40 dark:bg-blue-900/10 dark:text-blue-200">
          {t("stepInfo.filledFields", {
            defaultValue: "Pflichtfelder ausgefüllt",
          })}{" "}
          <span className="font-bold">
            {filledCount}/{required.length}
          </span>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-500 dark:text-slate-400">
          {t("stepInfo.checkBeforeContinue", {
            defaultValue: "Bitte prüfen Sie Ihre Angaben, bevor Sie fortfahren.",
          })}
        </div>

        <button
          type="button"
          disabled={!canNext}
          onClick={handleNext}
          className={`inline-flex min-w-[130px] items-center justify-center rounded-2xl px-5 py-3 text-sm font-semibold transition ${
            canNext
              ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20 hover:bg-blue-700"
              : "cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-slate-800 dark:text-slate-500"
          }`}
        >
          {t("stepInfo.next", { defaultValue: "Weiter" })}
        </button>
      </div>
    </div>
  );
}